import { useDeferredValue, useMemo, useState } from 'react';

import type { IconDefinition } from '@/sections/attacks/attackPaths/components/icons/registry';
import { getContextualLabel } from '@/sections/attacks/attackPaths/components/wizards/steps/IconPicker/contextLabels.config';

interface UseIconSearchResult {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredIcons: IconDefinition[];
  isFiltering: boolean;
  resultCount: number;
}

/**
 * Hook for searching icons with support for contextual labels.
 *
 * Searches across icon label, id, category, and (when categoryContext is provided)
 * the contextual label with External/Internal prefix.
 *
 * @param icons - Icons to search through
 * @param categoryContext - Optional category context for contextual label search
 *                          (e.g., 'internet' for External prefix, 'intranet' for Internal)
 * @returns Search state and filtered icons
 *
 * @example
 * ```tsx
 * // Without context - searches base labels only
 * const { filteredIcons } = useIconSearch(icons);
 *
 * // With context - also searches contextual labels ("External Server", "Internal Server")
 * const { filteredIcons } = useIconSearch(icons, 'internet');
 * ```
 */
export function useIconSearch(
  icons: IconDefinition[],
  categoryContext?: string
): UseIconSearchResult {
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const filteredIcons = useMemo(() => {
    if (!deferredSearchTerm) return icons;
    const term = deferredSearchTerm.toLowerCase();

    return icons.filter(icon => {
      // Search base label
      if (icon.label.toLowerCase().includes(term)) return true;

      // Search icon id
      if (icon.id.toLowerCase().includes(term)) return true;

      // Search category
      if (icon.category.toLowerCase().includes(term)) return true;

      // Search contextual label (when category context is provided)
      if (categoryContext) {
        const contextLabel = getContextualLabel(icon, categoryContext);
        if (contextLabel.toLowerCase().includes(term)) return true;
      }

      return false;
    });
  }, [icons, deferredSearchTerm, categoryContext]);

  return {
    searchTerm,
    setSearchTerm,
    filteredIcons,
    isFiltering: searchTerm !== deferredSearchTerm,
    resultCount: filteredIcons.length,
  };
}
